import React from 'react';
import { MessageCircle, FileText, Shield, Target } from 'lucide-react';

const WhyChoose = () => {
  const whyChooseData = {
    title: "Fostering Informed Marriages Built on Clarity and Communication",
    subtitle: "A beautiful wedding may last a day, but a well-structured marriage lasts a lifetime. Through our specialized pre-marital mediation programs, we help couples build a foundation of trust, transparency, and mutual understanding — bridging the emotional, practical, and legal dimensions of marriage.",
    features: [
      {
        id: 1,
        title: "Guided Communication",
        description: "Master the art of meaningful dialogue with structured frameworks that help you navigate complex topics with empathy and clarity.",
        icon: "MessageCircle"
      },
      {
        id: 2,
        title: "Structured Agreements",
        description: "Create a personalized Partnership Accord that documents your shared values, expectations, and commitments for a harmonious future.",
        icon: "FileText"
      }
    ]
  };

  const additionalFeatures = [
    {
      icon: Shield,
      title: 'Confidential & Safe',
      description: 'Your conversations remain completely private and secure'
    },
    {
      icon: Target,
      title: 'Result-Oriented',
      description: 'Tangible outcomes with actionable frameworks and agreements'
    }
  ];

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #d97706 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h3 className="text-amber-600 font-semibold text-lg uppercase tracking-wide mb-4">Why Choose Us</h3>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {whyChooseData.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {whyChooseData.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {whyChooseData.features.map((feature) => {
            const IconComponent = feature.icon === 'MessageCircle' ? MessageCircle : FileText;
            return (
              <div
                key={feature.id}
                className="group relative p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="mb-6 inline-flex p-4 rounded-xl bg-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="w-8 h-8 text-amber-600" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>

                {/* Decorative element */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/20 rounded-full blur-2xl group-hover:bg-amber-300/30 transition-colors"></div>
              </div>
            );
          })}

          {additionalFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={index}
                className="group relative p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="mb-6 inline-flex p-4 rounded-xl bg-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="w-8 h-8 text-amber-600" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>

                {/* Decorative element */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/20 rounded-full blur-2xl group-hover:bg-amber-300/30 transition-colors"></div>
              </div>
            );
          })}
        </div>

        {/* Testimonial with Image Grid */}
        <div className="mt-20 space-y-8">
          {/* Main Image */}
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1573495804664-b1c0849525af?w=1200&h=600&fit=crop&q=80"
                alt="Couple discussing partnership"
                className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>

          {/* Image Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {[
              'https://images.unsplash.com/photo-1560745155-a978f7ef4d06?w=400&h=400&fit=crop&q=80',
              'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=400&h=400&fit=crop&q=80',
              'https://images.unsplash.com/photo-1627964464837-6328f5931576?w=400&h=400&fit=crop&q=80',
              'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=400&fit=crop&q=80'
            ].map((img, index) => (
              <div
                key={index}
                className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <img
                  src={img}
                  alt={`Couple ${index + 1}`}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;